import React, { Fragment, FunctionComponent } from 'react'
import { Link } from 'react-router-dom'
import { displayDollars } from '../../helpers/displayFormatHelper'
import OfacNoteForm from './ofacNote'
import capitalize from 'lodash/capitalize'

import classnames from 'classnames'
import { Applicant, Loan } from './types'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  loan: Loan
  activeSection: string[]
  onSubmit: Callback<Callback>
}

export const Sidebar: FunctionComponent<Props> = ({
  loan,
  activeSection,
  onSubmit,
}) => {
  if (!loan) return null

  const { applicant, coApplicant, subject } = loan
  const { tradein } = subject
  let ofacTasks = loan.adminVerifications.filter((task) => {
    return task.taskType === 'REVIEW_OFAC'
  })

  const ofacTask = ofacTasks[0] || null
  const submitVerification = onSubmit(ofacTask)

  const renderApplicant = (applicant: Applicant, label: string) => {
    if (!applicant) return null

    const { user, currentCreditReport } = applicant
    const { firstName, lastName } = user

    return (
      <ul className="applicant summary-list">
        <li>
          {label}
          <span className="summary-value">
            <Link to={`/users/${user.id}`}>
              {firstName} {lastName}
            </Link>
          </span>
        </li>
        <li>
          Credit Score
          <span className="summary-value">{currentCreditReport.score}</span>
        </li>
        <OfacNoteForm
          ofacScore={applicant.ofacScore}
          adminVerifications={loan.adminVerifications}
          onSubmit={submitVerification}
        />
        <li>
          Precise Id Score
          <span className="summary-value">
            {applicant.preciseCreditReport.score}
          </span>
        </li>
      </ul>
    )
  }

  const isActive = (currentSection) => currentSection === activeSection

  const navItems = {
    'loan-app': 'Loan Application',
    history: 'Credit Decision History',
    'credit-report': 'Credit Report',
    tradelines: 'Tradeline Summary',
    rules: 'Policy & Rules',
  }

  const nav = Object.keys(navItems).map((key) => {
    const label = navItems[key]
    const active = isActive(key)
    const icon = active ? <i className="icon-arrow" /> : null
    return (
      <li className={classnames({ active })} key={key}>
        <Link to={`/underwriting/${loan.id}/${key}`}>
          {icon}
          <span>{label}</span>
        </Link>
      </li>
    )
  })
  const statusClass = (status: string) => `status-${status.toLowerCase()}`

  return (
    <div className="sidebar">
      <nav className="side-navigation">
        <ul>{nav}</ul>
      </nav>

      <div className="panel">
        <div className="panel-header">Application Summary</div>
        <div className="panel-header">
          {capitalize(loan.intent || '')}:
          <span className={statusClass(loan.adminStatus)}>
            {capitalize(loan.adminStatus || '')}
          </span>
        </div>
        <div className="panel-content">
          {renderApplicant(applicant, 'Applicant')}
          {coApplicant && (
            <Fragment>
              <hr />
              {}
              {renderApplicant(coApplicant, 'Co-Applicant')}
            </Fragment>
          )}
          <hr />
          <ul className="summary-list">
            <li>
              Credit Tier
              <span className="summary-value uppercase">{loan.tier}</span>
            </li>
          </ul>
          <hr />
          <ul className="summary-list">
            <li>
              Sales Price
              <span className="summary-value">
                {displayDollars(subject.amount)}
              </span>
            </li>
            <li>
              Down Payment
              <span className="summary-value">
                {displayDollars(subject.downPayment)}
              </span>
            </li>
            {tradein && (
              <Fragment>
                <li>
                  Trade-In Allowance
                  <span className="summary-value">
                    {displayDollars(tradein.allowance)}
                  </span>
                </li>
                <li>
                  Trade-In Sales Price
                  <span className="summary-value">
                    {displayDollars(tradein.amount)}
                  </span>
                </li>
              </Fragment>
            )}
            <li>
              Taxes &amp; Fees
              <span className="summary-value">
                {displayDollars(
                  parseFloat(subject.lienFee) +
                    parseFloat(subject.tax) +
                    parseFloat(subject.titleFee)
                )}
              </span>
            </li>
            <li>
              Warranty Amount
              <span className="summary-value">
                {loan.warrantyFee && displayDollars(loan.warrantyFee)}
              </span>
            </li>
            <li>
              Total Amount Financed
              <span className="summary-value">
                {displayDollars(subject.amountFinanced)}
              </span>
            </li>
          </ul>
          <hr />
          <ul className="summary-list">
            <li>
              Annual Percentage Rate
              <span className="summary-value">{loan.apr}</span>
            </li>
            <li>
              Term Length
              <span className="summary-value">{loan.term}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
